// src/pages/DeliverySettings.tsx
import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Divider,
} from "@mui/material";
import { getAppSettings, updateAppSettings } from "../api/adminSettingsApi";
import NumericTextField from "../components/inputs/NumericTextField";

export default function DeliverySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [settings, setSettings] = useState({
  local_delivery_enabled: false,
  default_delivery_fee_cents: 0,
  use_zones_for_delivery: true,
  delivery_eta_start: "30m",
  delivery_eta_end: "60m",
  free_delivery_minimum: 0,
  wholesale_free_delivery_minimum: 0,
  backorder_policy: "none" as "none" | "wholesale_only" | "both",
  wholesale_min_order_value: 0,
  show_stock_count_shop: true,
  show_stock_count_product_detail: true,
});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getAppSettings();
        setSettings(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load app settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const updated = await updateAppSettings(settings);
      setSettings(updated.settings || updated);
      setSuccess("Settings saved successfully!");
    } catch (err) {
      console.error(err);
      setError("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 5, mx: "auto", display: "block" }} />;

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 5 }}>
      <Card>
        <CardHeader title="Delivery Settings" />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={settings.local_delivery_enabled}
                onChange={(e) =>
                  setSettings({ ...settings, local_delivery_enabled: e.target.checked })
                }
              />
            }
            label="Enable Local Delivery"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.use_zones_for_delivery}
                onChange={(e) =>
                  setSettings({ ...settings, use_zones_for_delivery: e.target.checked })
                }
              />
            }
            label="Use Delivery Zones"
            sx={{ mb: 2 }}
          />

          <NumericTextField
            fullWidth
            label="Default Delivery Fee (in dollars)"
            value={settings.default_delivery_fee_cents / 100}
            onValueChange={(n) =>
              setSettings({
                ...settings,
                default_delivery_fee_cents: Math.round(n * 100),
              })
            }
            allowEmpty
            fallbackValue={0}
            min={0}
            decimals={2}
            sx={{ mb: 2 }}
          />
          <NumericTextField
            fullWidth
            label="Free Delivery Minimum — Retail ($)"
            value={settings.free_delivery_minimum}
            onValueChange={(n) =>
              setSettings({
                ...settings,
                free_delivery_minimum: n,
              })
            }
            allowEmpty
            fallbackValue={0}
            min={0}
            decimals={2}
            sx={{ mb: 2 }}
          />
          <NumericTextField
            fullWidth
            label="Free Delivery Minimum — Wholesale ($)"
            value={settings.wholesale_free_delivery_minimum ?? 0}
            onValueChange={(n) =>
              setSettings({
                ...settings,
                wholesale_free_delivery_minimum: n,
              })
            }
            allowEmpty
            fallbackValue={0}
            min={0}
            decimals={2}
            helperText="Applies to wholesale orders. 0 = no free delivery for wholesale."
            sx={{ mb: 2 }}
          />
          <NumericTextField
            fullWidth
            label="Minimum Order Value — Wholesale ($)"
            value={settings.wholesale_min_order_value ?? 0}
            onValueChange={(n) =>
              setSettings({
                ...settings,
                wholesale_min_order_value: n,
              })
            }
            allowEmpty
            fallbackValue={0}
            min={0}
            decimals={2}
            helperText="Approved wholesale customers must reach this subtotal to check out. 0 = no minimum. (Separate from per-product MOQ.)"
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Backorders (store default)
          </Typography>
          <TextField
            select
            fullWidth
            label="Allow backorders"
            value={settings.backorder_policy ?? "none"}
            onChange={(e) =>
              setSettings({
                ...settings,
                backorder_policy: e.target.value as "none" | "wholesale_only" | "both",
              })
            }
            helperText="Store-wide default for ordering past available stock. Each product can override this on its edit page."
            sx={{ mb: 2 }}
          >
            <MenuItem value="none">Off — never backorder</MenuItem>
            <MenuItem value="wholesale_only">Wholesale only</MenuItem>
            <MenuItem value="both">Retail &amp; wholesale</MenuItem>
          </TextField>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Storefront — stock display
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Show exact unit counts to customers, or just the availability status.
            When off, customers still see IN STOCK / LOW STOCK / OUT OF STOCK —
            only the number is hidden. (Currently applies to wholesale pages;
            retail already shows status only.)
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.show_stock_count_shop}
                onChange={(e) =>
                  setSettings({ ...settings, show_stock_count_shop: e.target.checked })
                }
              />
            }
            label="Show stock counts on shop / listing pages"
            sx={{ display: "block", mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.show_stock_count_product_detail}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    show_stock_count_product_detail: e.target.checked,
                  })
                }
              />
            }
            label="Show stock counts on product detail pages"
            sx={{ display: "block", mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="ETA Start"
              value={settings.delivery_eta_start}
              onChange={(e) =>
                setSettings({ ...settings, delivery_eta_start: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="ETA End"
              value={settings.delivery_eta_end}
              onChange={(e) =>
                setSettings({ ...settings, delivery_eta_end: e.target.value })
              }
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ mt: 2, fontWeight: 600 }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Note: If "Use Delivery Zones" is enabled, the system will calculate delivery fees
            based on the customer's address and matching polygon zone. Otherwise, the default
            delivery fee will be applied.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
